import { useEffect, useState } from "react";
import api from "@/lib/api";
import EmailNav from "./EmailNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, RefreshCw, Trash2, Search, Upload, X, Tag } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { email: "", first_name: "", last_name: "", phone: "", brand: "rrdc", tags: [] };

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("all");
  const [unsub, setUnsub] = useState("no");
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/email/contacts", { params: { q, brand, unsubscribed: unsub, limit: 200 } })
      .then(({ data }) => { setContacts(data.contacts || []); setTotal(data.total || 0); })
      .catch(() => toast.error("Failed to load contacts"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [brand, unsub]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const create = async () => {
    if (!form.email) return toast.error("Email required");
    try {
      await api.post("/email/contacts", form);
      toast.success("Contact added");
      setForm(EMPTY); setShowAdd(false); setTagInput(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact? This cannot be undone.")) return;
    try { await api.delete(`/email/contacts/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleUnsub = async (c) => {
    try {
      await api.patch(`/email/contacts/${c.id}`, { unsubscribed: !c.unsubscribed });
      toast.success(c.unsubscribed ? "Resubscribed" : "Unsubscribed");
      load();
    } catch { toast.error("Failed"); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post("/email/contacts/sync-from-leads");
      toast.success(`+${r.created} new, ${r.updated} updated`);
      load();
    } catch { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  const importCsv = async () => {
    if (!csvText.trim()) return toast.error("Paste CSV first");
    try {
      const { data: r } = await api.post("/email/contacts/import", csvText, { headers: { "Content-Type": "text/csv" } });
      toast.success(`+${r.created} new, ${r.updated} updated, ${r.skipped} skipped`);
      setShowImport(false); setCsvText(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
  };

  const addTag = () => {
    if (!tagInput.trim()) return;
    if (form.tags.includes(tagInput.trim())) return;
    setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
    setTagInput("");
  };

  return (
    <div data-testid="contacts-page">
      <EmailNav />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">Contacts</h2>
          <p className="text-xs text-muted-foreground">{total} total</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={syncNow} disabled={syncing} data-testid="contacts-sync-btn">
            <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} /> Sync from Leads
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="contacts-import-btn">
            <Upload size={14} className="mr-2" /> Import CSV
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="contacts-add-btn">
            <Plus size={14} className="mr-2" /> Add Contact
          </Button>
        </div>
      </div>

      <form onSubmit={onSearch} className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="contacts-search" placeholder="Search name or email..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className="w-[140px]" data-testid="contacts-brand-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All brands</SelectItem>
            <SelectItem value="rrdc">RRDC</SelectItem>
            <SelectItem value="riverview">Riverview</SelectItem>
          </SelectContent>
        </Select>
        <Select value={unsub} onValueChange={setUnsub}>
          <SelectTrigger className="w-[160px]" data-testid="contacts-unsub-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="no">Subscribed only</SelectItem>
            <SelectItem value="yes">Unsubscribed only</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" variant="outline" size="sm">Search</Button>
      </form>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              No contacts. Click "Sync from Leads" to import everyone who has submitted an inquiry.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {contacts.map((c) => (
                <div key={c.id} data-testid={`contact-row-${c.id}`} className="p-3 flex items-center gap-3 flex-wrap hover:bg-accent/20 transition-colors">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium text-sm">{c.first_name || "—"} {c.last_name}</div>
                    <div className="text-xs text-muted-foreground">{c.email}</div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className="text-[10px] capitalize">{c.brand}</Badge>
                    {c.unsubscribed && <Badge className="text-[10px] bg-rose-500/15 text-rose-500">Unsubscribed</Badge>}
                    {c.source === "lead" && <Badge variant="outline" className="text-[10px]">From Lead</Badge>}
                    {c.tags?.slice(0, 3).map((t) => <Badge key={t} variant="secondary" className="text-[10px]"><Tag size={9} className="mr-0.5" />{t}</Badge>)}
                  </div>
                  <div className="text-xs text-muted-foreground hidden md:block">
                    Sent: {c.engagement?.sent || 0} · Opens: {c.engagement?.opens || 0} · Clicks: {c.engagement?.clicks || 0}
                  </div>
                  <Button data-testid={`contact-toggle-unsub-${c.id}`} variant="ghost" size="sm" onClick={() => toggleUnsub(c)}>
                    {c.unsubscribed ? "Resubscribe" : "Unsubscribe"}
                  </Button>
                  <Button data-testid={`contact-delete-${c.id}`} variant="ghost" size="icon" onClick={() => remove(c.id)}>
                    <Trash2 size={15} className="text-rose-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="add-contact-email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <Input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rrdc">Rolling River</SelectItem>
                <SelectItem value="riverview">Riverview</SelectItem>
                <SelectItem value="any">Any / Both</SelectItem>
              </SelectContent>
            </Select>
            <div>
              <div className="flex gap-2">
                <Input placeholder="Tag (e.g. VIP)" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              <div className="flex gap-1 flex-wrap mt-2">
                {form.tags.map((t) => (
                  <Badge key={t} variant="secondary" className="gap-1">
                    {t}
                    <button onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })}><X size={12} /></button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={create} data-testid="add-contact-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV import dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Import Contacts (CSV)</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            <b>Required header:</b> <span className="font-mono">email</span>.<br/>
            <b>Recognized headers</b> (any order, case-insensitive):
            <span className="font-mono"> first_name, last_name, email, sport, phone, tags, brand</span>.
            <br/>The <span className="font-mono">sport</span> column is recorded as a tag (lowercased) AND in <span className="font-mono">custom_fields.sport</span> — so you can filter campaigns by sport later.
          </p>

          {/* File picker */}
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="file"
              accept=".csv,text/csv"
              data-testid="import-csv-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const reader = new FileReader();
                reader.onload = () => setCsvText(String(reader.result || ""));
                reader.readAsText(f);
              }}
              className="text-xs"
            />
            <button
              type="button"
              className="text-xs text-primary underline"
              onClick={async () => {
                try {
                  const resp = await api.get("/email/contacts/import-template.csv", { responseType: "blob" });
                  const url = URL.createObjectURL(resp.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = "email-contacts-template.csv"; a.click();
                  URL.revokeObjectURL(url);
                } catch { toast.error("Could not fetch template"); }
              }}
              data-testid="download-csv-template-btn"
            >
              Download sample template
            </button>
          </div>

          <textarea
            data-testid="import-csv-textarea"
            className="w-full h-48 rounded-md border border-border bg-background p-3 font-mono text-xs"
            placeholder={"first_name,last_name,email,sport,phone,tags\nJane,Doe,jane@example.com,Soccer,555-1234,VIP;parent\nJohn,Smith,john@example.com,Hockey,,\n"}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)}>Cancel</Button>
            <Button onClick={importCsv} data-testid="import-csv-go">Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
